<template>
  <ConditionalLayout>
    <main class="how-it-works-page">
      <!-- Main Content -->
      <div class="content">
        <div class="hero-section">
          <h1>How it Works</h1>
        </div>



        <!-- Examples Section -->
        <section class="examples-section" aria-label="Usage examples">
          <h2>See Prec8 in Action</h2>
          <p class="section-subtitle">Here's how team recognition looks in your Slack workspace</p>
          
          <div class="slack-examples">
            <article class="message-row">
              <div class="slack-message">
                <div class="message-content">
                  <div class="message-header">
                    <span class="username">John Smith</span>
                  </div>
                  <div class="message-text">
                    Great work on the new feature release! <span class="mention">@ari</span> your attention to detail really made this launch successful. {{ getCurrentEmoji() }}
                  </div>
                </div>
              </div>
              <div class="message-explanation">
                <div class="explanation-title">Custom Emoji Recognition</div>
                <div class="explanation-text">Every team has their own vibe! Pick the emoji that feels right for your crew's way of saying "you rock!"</div>
                <div class="emoji-dropdown">
                  <div class="emoji-option" :class="{ selected: selectedEmojiIndex === 0 }" role="button" tabindex="0" @click="selectedEmojiIndex = 0" @keydown.enter="selectedEmojiIndex = 0">🙏</div>
                  <div class="emoji-option" :class="{ selected: selectedEmojiIndex === 1 }" role="button" tabindex="0" @click="selectedEmojiIndex = 1" @keydown.enter="selectedEmojiIndex = 1">👏</div>
                  <div class="emoji-option" :class="{ selected: selectedEmojiIndex === 2 }" role="button" tabindex="0" @click="selectedEmojiIndex = 2" @keydown.enter="selectedEmojiIndex = 2">🍩</div>
                  <div class="emoji-option" :class="{ selected: selectedEmojiIndex === 3 }" role="button" tabindex="0" @click="selectedEmojiIndex = 3" @keydown.enter="selectedEmojiIndex = 3">💪</div>
                  <div class="emoji-option" :class="{ selected: selectedEmojiIndex === 4 }" role="button" tabindex="0" @click="selectedEmojiIndex = 4" @keydown.enter="selectedEmojiIndex = 4">⭐</div>
                  <div class="emoji-option" :class="{ selected: selectedEmojiIndex === 5 }" role="button" tabindex="0" @click="selectedEmojiIndex = 5" @keydown.enter="selectedEmojiIndex = 5">🏆</div>
                </div>
                <br/>
                <div class="explanation-text">In this example, <span class="mention">@ari</span> gets 1 <span class="appreciation-token-text">appreciation token</span></div>
              </div>
            </article>

            <article class="message-row">
              <div class="slack-message">
                <div class="message-content">
                  <div class="message-header">
                    <span class="username">Mike Brown</span>
                  </div>
                  <div class="message-text">
                    🙏 🙏 🙏 Just wanted to recognize <span class="mention">@sarah</span> for her amazing presentation skills in the client meeting today. 
                  </div>
                </div>
              </div>
              <div class="message-explanation">
                <div class="explanation-title">Super Boost Recognition</div>
                <div class="explanation-text">Each emoji corresponds to one <span class="appreciation-token-text">appreciation token</span>, in this instance <span class="mention">@sarah</span> gets 3. Stack 'em up for extra impact! </div>
              </div>
            </article>

            <article class="message-row">
              <div class="slack-message">
                <div class="message-content">
                  <div class="message-header">
                    <span class="username">Alice Johnson</span>
                  </div>
                  <div class="message-text">
                    Thanks 🙏 <span class="mention">@steve</span> and <span class="mention">@alex</span> for the help with debugging yesterday!  Couldn't have solved it without you. 
                  </div>
                </div>
              </div>
              <div class="message-explanation">
                <div class="explanation-title">Team Recognition</div>
                <div class="explanation-text">Spread the love! Tag multiple people and they all get points. <span class="mention">@steve</span> and <span class="mention">@alex</span> both score appreciation tokens! 🎯</div>
              </div>
            </article>
          </div>
        </section>

        <!-- Features Deep Dive -->
        <section class="features-deep-dive" aria-label="Detailed features">
          <h2>Key Features That Make Recognition Easy</h2>
          
          <div class="feature-details">
            <div class="feature-detail">
              <h3>🎯 Daily Point System</h3>
              <p>Every team member gets 10 points to give each day, ensuring recognition happens regularly and fairly. Points reset at midnight in your workspace's timezone.</p>
            </div>
            
            <div class="feature-detail">
              <h3>📊 Real-Time Analytics</h3>
              <p>Track recognition patterns, see who's giving and receiving the most appreciation, and understand your team's culture through data.</p>
            </div>
            
            <div class="feature-detail">
              <h3>🏆 Leaderboards & Rewards</h3>
              <p>Friendly competition motivates teams. Use points to redeem rewards and celebrate achievements together.</p>
            </div>
            
            <div class="feature-detail">
              <h3>🔒 Privacy & Security</h3>
              <p>All recognition data stays within your workspace. Enterprise-grade security ensures your team's information is protected.</p>
            </div>
          </div>
        </section>

        <!-- Getting Started Section - Only for unauthenticated users -->
        <GettingStarted v-if="!isAuthenticated" />

        <!-- FAQ Section -->
        <section class="faq-section" aria-label="Frequently asked questions">
          <h2>Frequently Asked Questions</h2>
          
          <div class="faq-list">
            <details class="faq-item">
              <summary>How many points can I give each day?</summary>
              <p>Each team member gets 10 points to give daily. These reset at midnight in your workspace's timezone, encouraging regular recognition.</p>
            </details>
            
            <details class="faq-item">
              <summary>Can I give points to multiple people at once?</summary>
              <p>Yes! Tag multiple people in your message and they'll all receive points. Each emoji equals one point per person.</p>
            </details>
            
            <details class="faq-item">
              <summary>What happens to the points I receive?</summary>
              <p>Points you receive become redeemable tokens that you can use for rewards, or they can be tracked as part of your recognition history.</p>
            </details>
            

          </div>
        </section>
      </div>
    </main>
  </ConditionalLayout>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import ConditionalLayout from './components/ConditionalLayout.vue'
import GettingStarted from './components/GettingStarted.vue'
import { useAuthStore } from './stores/authStore'

@Options({
  components: {
    ConditionalLayout,
    GettingStarted
  },
})
export default class HowItWorks extends Vue {
  selectedEmojiIndex = 0;
  isAuthenticated = false;

  async mounted() {
    // Check authentication status
    try {
      const { checkAuth } = useAuthStore();
      const authData = await checkAuth();
      this.isAuthenticated = authData.isAuthenticated;
    } catch (error) {
      this.isAuthenticated = false;
    }

    // Rotate through emojis every 2 seconds
    setInterval(() => {
      this.selectedEmojiIndex = (this.selectedEmojiIndex + 1) % 6;
    }, 2000);
  }

  getCurrentEmoji() {
    const emojis = ['🙏', '👏', '🍩', '💪', '⭐', '🏆'];
    return emojis[this.selectedEmojiIndex];
  }

  goHome() {
    this.$router.push("/");
  }
}
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;600&display=swap');

.how-it-works-page {
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: #1a1a1a;
  line-height: 1.5;
  min-height: 100vh;
  background: #f8fafc;
}

.highlight {
  background: linear-gradient(120deg, #FF6B6B 0%, #FF8E53 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background: #ffffff;
  padding: 1rem 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;

  .header-content {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .header-logo {
    height: 100px;
    width: auto;
    object-fit: contain;
    cursor: pointer;
  }

  .header-nav {
    display: flex;
    gap: 1.5rem;
  }

  .nav-link {
    background: none;
    border: none;
    color: #666;
    font-size: 1rem;
    cursor: pointer;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    transition: background-color 0.2s ease, color 0.2s ease;

    &:hover {
      background-color: #f0f0f0;
      color: #1a1a1a;
    }
  }
}

.content {
  padding-left: 2rem;
  padding-right: 2rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}

.hero-section {
  text-align: center;
  margin-bottom: 2rem;
  padding: 2rem 0;

  h1 {
    font-size: 3.5rem;
    font-weight: 800;
    margin-bottom: 0.5rem;
    color: #1a1a1a;
  }

  .hero-subtitle {
    font-size: 1.25rem;
    color: #666;
  }
}



.examples-section {
  padding: 3rem 0;
  background: #f8fafc;

  h2 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #1a1a1a;
    text-align: center;
  }

  .section-subtitle {
    font-size: 1.25rem;
    color: #666;
    text-align: center;
    margin-bottom: 3rem;
  }

  .slack-examples {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    margin-bottom: 3rem;
  }

  .message-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    align-items: start;
  }

  .slack-message {
    background: white;
    border-radius: 12px;
    padding: 0.75rem 1rem;
    border: 1px solid #e1e5e9;

    .message-content {
      .message-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.25rem;

        .username {
          font-weight: 600;
          color: #1a1a1a;
          font-size: 0.9rem;
        }

        .timestamp {
          font-size: 0.8rem;
          color: #666;
        }
      }

      .message-text {
        font-size: 0.95rem;
        color: #333;
        line-height: 1.4;
        text-align: left;

        .mention {
          color: #1264A3;
          font-weight: 600;
          background-color: rgba(18, 100, 163, 0.1);
          padding: 0.1rem 0.3rem;
          border-radius: 4px;
        }
      }
    }
  }

  .message-explanation {
    padding: 1rem;
    text-align: left;

    .explanation-title {
      font-weight: 600;
      color: #1a1a1a;
      font-size: 1rem;
      margin-bottom: 0.75rem;
      text-transform: none;
      letter-spacing: normal;
      text-align: left;
    }

    .explanation-text {
      color: #666;
      font-size: 0.95rem;
      line-height: 1.5;
      text-align: left;

      .appreciation-token-text {
        background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        font-weight: 700;
        font-size: 1.1em;
        text-shadow: 0 2px 4px rgba(255, 107, 107, 0.2);
        position: relative;
        display: inline-block;
        animation: tokenGlow 2s ease-in-out infinite;
      }
    }

    .emoji-dropdown {
      display: flex;
      gap: 0.5rem;
      margin-top: 0.75rem;
      flex-wrap: wrap;

      .emoji-option {
        background: #f8fafc;
        border: 2px solid #e1e5e9;
        border-radius: 8px;
        padding: 0.5rem;
        font-size: 1.2rem;
        cursor: pointer;
        transition: all 0.3s ease;

        &:hover {
          border-color: #FF6B6B;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(255, 107, 107, 0.2);
        }

        &.selected {
          border-color: #FF6B6B;
          background: rgba(255, 107, 107, 0.1);
          transform: scale(1.1);
          box-shadow: 0 6px 20px rgba(255, 107, 107, 0.3);
        }
      }
    }
  }
}

.features-deep-dive {
  padding: 3rem 0;
  background: #f8fafc;

  h2 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #1a1a1a;
    text-align: center;
  }

  .feature-details {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    text-align: left;
  }

  .feature-detail {
    background: white;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

    h3 {
      font-size: 1.5rem;
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 0.75rem;
    }

    p {
      font-size: 1rem;
      color: #666;
      line-height: 1.6;
    }
  }
}

.faq-section {
  padding: 3rem 0;
  background: #f8fafc;

  h2 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #1a1a1a;
    text-align: center;
  }

  .faq-list {
    max-width: 800px;
    margin: 0 auto;
  }

  .faq-item {
    background: white;
    border-radius: 12px;
    margin-bottom: 1rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

    summary {
      font-size: 1.25rem;
      font-weight: 600;
      color: #1a1a1a;
      padding: 1rem;
      cursor: pointer;
      list-style: none;
      position: relative;

      &::after {
        content: "▼";
        position: absolute;
        right: 1rem;
        transition: transform 0.3s ease;
      }

      &[open]::after {
        transform: rotate(180deg);
      }
    }

    p {
      font-size: 1rem;
      color: #666;
      padding: 1rem;
      line-height: 1.6;
    }
  }
}

@media (max-width: 768px) {
  .content {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .hero-section {
    h1 {
      font-size: 2.5rem;
    }

    .hero-subtitle {
      font-size: 1rem;
    }
  }



  .examples-section {
    .slack-examples {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }

    .message-row {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
  }

  .features-deep-dive {
    .feature-details {
      grid-template-columns: 1fr;
    }
  }

  .faq-section {
    .faq-item summary {
      font-size: 1.1rem;
    }
  }
}

@keyframes tokenGlow {
  0%, 100% {
    filter: brightness(1) drop-shadow(0 0 5px rgba(255, 107, 107, 0.3));
  }
  50% {
    filter: brightness(1.1) drop-shadow(0 0 8px rgba(255, 107, 107, 0.5));
  }
}
</style> 